import { TranslationRecord } from '~src/localization/translationKeys';
import {
  ConsumptionProfile,
  EnergyLabelEnum,
  HeatDistributionTypeEnum,
  MonthlyConsumptionProfile,
  RenovationDegreeEnum,
  ResidenceType,
  RoofShadows,
} from '~src/types';

export const ENERGY_LABEL_OPTIONS: { label?: string; value?: EnergyLabelEnum }[] = [
  { value: 'A2020' },
  { value: 'A2015' },
  { value: 'A2010' },
  { value: 'B' },
  { value: 'C' },
  { value: 'D' },
  { value: 'E' },
  { value: 'F' },
  { value: 'G' },
  { value: undefined, label: '-' },
];

export const getConsumptionProfileOptions = (
  translate: TranslationRecord
): Array<{
  label: string;
  value: ConsumptionProfile;
}> => [
  {
    value: 'homeInAfternoon',
    label: translate.I_COME_HOME_IN_THE_AFTERNOON,
  },
  {
    value: 'homeAt17',
    label: translate.I_COME_HOME_AT_17,
  },
  {
    value: 'homeInMorning',
    label: translate.I_AM_HOME_IN_THE_MORNING,
  },
  {
    value: 'homeMostDays',
    label: translate.I_AM_HOME_MOST_DAYS,
  },
  {
    value: 'homeEveryDay',
    label: translate.I_AM_HOME_EVERY_DAY,
  },
  {
    value: 'industrialConsumption',
    label: translate.INDUSTRIAL,
  },
];

export const getMonthlyConsumptionProfileOptions = (
  translate: TranslationRecord
): Array<{
  label: string;
  value: MonthlyConsumptionProfile;
}> => [
  {
    value: 'privateDomicile',
    label: translate.PRIVATE_DOMICILE,
  },
  {
    value: 'industrial',
    label: translate.INDUSTRIAL,
  },
];

export const getHeatDistributionTypeOptions = (
  translate: TranslationRecord
): Array<{
  label: string;
  value: HeatDistributionTypeEnum;
}> => [
  { value: 'radiator', label: translate.RADIATOR },
  { value: 'floor', label: translate.FLOOR_HEATING },
  {
    value: 'radiatorAndFloor',
    label: `${translate.RADIATOR} ${translate.AND.toLowerCase()} ${translate.FLOOR_HEATING.toLowerCase()}`,
  },
];

export const getRenovationDegreeOptions = (
  translate: TranslationRecord
): Array<{
  label: string;
  value: RenovationDegreeEnum;
}> => [
  { value: 'asBuilt', label: translate.AS_BUILT },
  { value: 'medium', label: translate.MEDIUM },
  { value: 'maximum', label: translate.MAXIMUM },
];

export const getResidenceTypeOptions = (
  translate: TranslationRecord
): Array<{
  label: string;
  value: ResidenceType;
}> => [
  { value: 'villa', label: `${translate.VILLA}/${translate.DETACHED_HOUSE}` },
  { value: 'townhouse', label: translate.TERRACED_HOUSE },
  { value: 'apartment', label: translate.APARTMENT },
  { value: 'summerhouse', label: translate.SUMMER_HOUSE },
];

export const getRoofOrientationOptions = (
  translate: TranslationRecord
): Array<{
  label: string;
  value: number;
}> => {
  const { NORTH, EAST, SOUTH, WEST, NORTH_LETTER: N, EAST_LETTER: E, SOUTH_LETTER: S, WEST_LETTER: W } = translate;

  return [
    { label: `${NORTH} (0°)`, value: 180 },
    { label: `${N}${N}${N}${E} (15°)`, value: 165 },
    { label: `${N}${N}${E} (30°)`, value: 150 },
    { label: `${NORTH}${EAST.toLowerCase()} (45°)`, value: 135 },
    { label: `${N}${E}${E}${E} (60°)`, value: 120 },
    { label: `${N}${E}${E} (75°)`, value: 105 },
    { label: `${EAST} (90°)`, value: 90 },
    { label: `${S}${E}${E}${E} (105°)`, value: 75 },
    { label: `${S}${E}${E} (120°)`, value: 60 },
    { label: `${SOUTH}${EAST.toLowerCase()} (135°)`, value: 45 },
    { label: `${S}${S}${S}${E} (150°)`, value: 30 },
    { label: `${S}${S}${E} (165°)`, value: 15 },
    { label: `${SOUTH} (180°)`, value: 0 },
    { label: `${S}${S}${S}${W} (195°)`, value: -15 },
    { label: `${S}${S}${W} (210°)`, value: -30 },
    { label: `${SOUTH}${WEST.toLowerCase()} (225°)`, value: -45 },
    { label: `${S}${W}${W} (240°)`, value: -60 },
    { label: `${S}${W}${W}${W} (255°)`, value: -75 },
    { label: `${WEST} (270°)`, value: -90 },
    { label: `${N}${W}${W} (285°)`, value: -105 },
    { label: `${N}${W}${W}${W} (300°)`, value: -120 },
    { label: `${NORTH}${WEST.toLowerCase()} (315°)`, value: -135 },
    { label: `${N}${N}${W} (330°)`, value: -150 },
    { label: `${N}${N}${N}${W} (345°)`, value: -165 },
  ];
};

export const getRoofTypeOptions = (
  translate: TranslationRecord
): Array<{
  label: string;
  value: string;
}> => [
  { label: `${translate.SITE_VISIT_ANSWER_ROOF_TYPE_BETON_TEGL}`, value: 'concreteTile' },
  { label: `${translate.SITE_VISIT_ANSWER_ROOF_TYPE_LER_TEGL}`, value: 'clayTile' },
  { label: `${translate.SITE_VISIT_ANSWER_ROOF_TYPE_METAL}`, value: 'metal' },
  { label: `${translate.SITE_VISIT_ANSWER_ROOF_TYPE_ETERNIT}`, value: 'fiberCement' },
  { label: `${translate.SITE_VISIT_ANSWER_ROOF_TYPE_FLAT}`, value: 'flat' },
  { label: `${translate.SITE_VISIT_ANSWER_ROOF_TYPE_TAGPAP}`, value: 'felt' },
  { label: `${translate.OTHER}`, value: 'other' },
  { label: `${translate.UNKNOWN}`, value: 'unknown' },
];

export const getRoofLivingSpaceOptions = (
  translate: TranslationRecord
): Array<{
  label: string;
  value: string;
}> => [
  { value: 'no', label: translate.NO },
  { value: 'yesLoftrum', label: translate.YES_LOFTRUM },
  { value: 'yesLoftTilKip', label: translate.YES_LOFT_TIL_KIP },
];

export const getRoofShadowOptions = (
  translate: TranslationRecord
): Array<{
  label: string;
  value: RoofShadows;
}> => [
  { value: 'none', label: translate.NO_SHADE },
  { value: 'some', label: translate.PARTIALLY_SHADY },
  { value: 'many', label: translate.VERY_SHADY },
];

export const tryParseStringFloat = (input: string | number | null | undefined) =>
  input ? parseFloat(input.toString().replaceAll(',', '.')) : undefined;
